import { useContext } from 'react';
import { ProjectPermissionContext } from '../contexts/ProjectPermissionContext';
import { useAuth } from '../contexts/AuthContext';

/**
 * Hook for project-specific permissions
 * Use ONLY inside ProjectPermissionProvider
 * For global permissions, use useGlobalPermission instead
 */
export const useProjectPermission = () => {
  const context = useContext(ProjectPermissionContext);
  const { user } = useAuth();

  // ✅ If context is not available (outside ProjectPermissionProvider)
  // Return default permissions based on user role only
  if (!context) {
    const isAdmin = user?.role === 'company_admin' || user?.role === 'super_admin';
    
    return {
      // Role checks based on global user role
      isAdmin,
      isProjectManager: false,
      isSiteEngineer: false,
      isSupervisor: false,
      isViewer: false,

      // Basic permissions based on company role
      can: (permission) => isAdmin,
      canAny: (permissionArray) => isAdmin,
      canAll: (permissionArray) => isAdmin,

      // Specific permissions
      canViewBudget: isAdmin,
      canEditBudget: isAdmin,
      canEditProgress: isAdmin,
      canManageTasks: isAdmin,
      canExportData: isAdmin,
      canDelete: isAdmin,

      // Context info
      role: isAdmin ? 'admin' : null,
      permissions: isAdmin ? getAllPermissions() : {},
      hasAccess: isAdmin,
      loading: false,
      isProjectContext: false,
    };
  }

  // ✅ Inside ProjectPermissionProvider - use full context
  return {
    isAdmin: context.isAdmin,
    isProjectManager: context.isProjectManager,
    isSiteEngineer: context.isSiteEngineer,
    isSupervisor: context.isSupervisor,
    isViewer: context.isViewer,

    can: (permission) => context.permissions[permission] || false,
    canAny: (permissionArray) => permissionArray.some(p => context.permissions[p]),
    canAll: (permissionArray) => permissionArray.every(p => context.permissions[p]),

    canViewBudget: context.permissions.canViewBudget,
    canEditBudget: context.permissions.canEditBudget,
    canEditProgress: context.permissions.canEditProgress,
    canManageTasks: context.permissions.canManageTasks,
    canExportData: context.permissions.canExportData,
    canDelete: context.permissions.canDelete,

    role: context.projectRole,
    permissions: context.permissions,
    hasAccess: context.hasAccess,
    loading: context.loading,
    isProjectContext: true,
  };
};

// Helper function
const getAllPermissions = () => ({
  canView: true,
  canEdit: true,
  canDelete: true,
  canManageStaff: true,
  canViewBudget: true,
  canEditBudget: true,
  canExportData: true,
  canManageTasks: true,
  canEditProgress: true,
  canViewReports: true,
});
